/**
 * Camera Behavior
 *
 * Handles zoom/pan input and keyboard shortcuts for camera control.
 * Owns the ZoomAnimator for smooth mouse-wheel zoom.
 */

import type { InputSystem } from '@rool/input';
import { createZoomAnimator, type ZoomAnimator } from '@rool/input';
import type { ZoomContext, PanContext } from '@rool/input/types';
import type { DirectCanvas } from '@rool/renderer';
import type { Camera } from '@rool/renderer/types';
import type { Point } from '../types';

// =============================================================================
// Types
// =============================================================================

export interface CameraConfig {
	minZoom: number;
	maxZoom: number;
}

export interface CameraBehaviorContext {
	input: InputSystem;
	canvas: DirectCanvas;
	container: HTMLElement;
	config: CameraConfig;
	onCameraChange?: (camera: Camera) => void;
	log?: (type: string, detail: string) => void;
}

// =============================================================================
// Camera Behavior
// =============================================================================

export function registerCameraBehavior(ctx: CameraBehaviorContext): () => void {
	const { input, canvas, container, config, onCameraChange, log } = ctx;
	const cleanups: (() => void)[] = [];

	function clientToScreen(clientX: number, clientY: number): Point {
		const rect = container.getBoundingClientRect();
		return { x: clientX - rect.left, y: clientY - rect.top };
	}

	function handleCameraChange(): void {
		onCameraChange?.(canvas.getCamera());
	}

	// =========================================================================
	// Zoom Animator (smooth mouse-wheel zoom)
	// =========================================================================

	const zoomAnimator: ZoomAnimator = createZoomAnimator({
		getCamera: () => canvas.getCamera(),
		getViewport: () => canvas.getViewport(),
		onZoom: (factor, origin) => {
			canvas.zoom(factor, origin.x, origin.y);
			handleCameraChange();
		},
		getZoomBounds: () => ({ min: config.minZoom, max: config.maxZoom }),
	});

	// =========================================================================
	// Zoom Input
	// =========================================================================

	cleanups.push(
		input.on('zoom', { mode: '*' }, (ctx) => {
			const zoomCtx = ctx as ZoomContext;
			const screen = clientToScreen(zoomCtx.origin.x, zoomCtx.origin.y);

			if (zoomCtx.source === 'mouse' && zoomCtx.deltaY !== undefined) {
				// Mouse wheel - enqueue for smooth animation
				zoomAnimator.enqueue(zoomCtx.deltaY, screen);
			} else {
				// Trackpad gesture - apply directly (OS provides smooth gestures)
				canvas.zoom(zoomCtx.factor, screen.x, screen.y);
				handleCameraChange();
			}
		}),
	);

	// =========================================================================
	// Pan Input
	// =========================================================================

	cleanups.push(
		input.on('pan', { mode: '*' }, (ctx) => {
			const panCtx = ctx as PanContext;
			canvas.pan(panCtx.delta.x, panCtx.delta.y);
			handleCameraChange();
		}),
	);

	// =========================================================================
	// Keyboard Shortcuts
	// =========================================================================

	cleanups.push(
		input.on(
			'keydown',
			'mod+=',
			{ mode: 'default', command: 'Zoom In', category: 'View' },
			(ctx) => {
				ctx.stopPropagation();
				const viewport = canvas.getViewport();
				canvas.zoom(1.25, viewport.width / 2, viewport.height / 2);
				handleCameraChange();
				log?.('zoom', `${(canvas.getCamera().zoom * 100).toFixed(0)}% (keyboard)`);
			},
		),
	);

	cleanups.push(
		input.on(
			'keydown',
			'mod+-',
			{ mode: 'default', command: 'Zoom Out', category: 'View' },
			(ctx) => {
				ctx.stopPropagation();
				const viewport = canvas.getViewport();
				canvas.zoom(0.8, viewport.width / 2, viewport.height / 2);
				handleCameraChange();
				log?.('zoom', `${(canvas.getCamera().zoom * 100).toFixed(0)}% (keyboard)`);
			},
		),
	);

	cleanups.push(
		input.on(
			'keydown',
			'mod+0',
			{ mode: 'default', command: 'Reset Zoom', category: 'View' },
			(ctx) => {
				ctx.stopPropagation();
				const cam = canvas.getCamera();
				canvas.setCamera({ ...cam, zoom: 1 });
				handleCameraChange();
				log?.('zoom', '100% (reset)');
			},
		),
	);

	return () => {
		zoomAnimator.destroy();
		for (const cleanup of cleanups) {
			cleanup();
		}
	};
}
